import { FileUp } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";

const BATCH = 300;
const n = (v) => Number(v || 0).toLocaleString("fr-FR");

// Import de l'export JSON noCRM : le navigateur lit le fichier et l'envoie par paquets de 300 fiches
export const ExportImport = () => {
  const [rows, setRows] = useState(null);
  const [reading, setReading] = useState(false);
  const [ids, setIds] = useState("");
  const [progress, setProgress] = useState(null);
  const [st, setSt] = useState({});
  const refresh = () => api.get("/migration/import/status").then(({ data }) => setSt(data)).catch(() => {});
  useEffect(() => { refresh(); }, []);
  const read = (file) => {
    setRows(null);
    if (!file) return;
    setReading(true);
    file.text().then((txt) => {
      const data = JSON.parse(txt);
      if (!Array.isArray(data) || !data.length || typeof data[0].id !== "number") throw new Error("format");
      setRows(data);
    }).catch(() => toast.error("Ce fichier n'est pas un export JSON de fiches noCRM.")).finally(() => setReading(false));
  };
  const run = async (test) => {
    const only = test ? ids.split(/[\s,;]+/).map(Number).filter(Boolean) : [];
    if (test && !only.length) { toast.error("Indiquez les numéros noCRM des fiches à tester."); return; }
    if (!test && !window.confirm(`Importer les ${n(rows.length)} fiches ? Les fiches pas encore copiées seront remplies (une sauvegarde est faite avant). Les appels Ringover et l'activité du CRM ne sont pas touchés.`)) return;
    const list = test ? rows.filter((r) => only.includes(r.id)) : rows;
    if (!list.length) { toast.error("Aucune de ces fiches n'est dans le fichier."); return; }
    try {
      await api.post("/migration/import/start", { mode: test ? "test" : "complet", total: list.length, only_ids: only });
      setProgress(0);
    } catch (e) { toast.error(e.response?.data?.detail || "Import impossible."); return; }
    let error = null;
    for (let i = 0; i < list.length; i += BATCH) {
      let last = null;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try { await api.post("/migration/import/batch", { rows: list.slice(i, i + BATCH) }); last = null; break; } catch (e) {
          last = e;
          if ((e.response?.status && e.response.status < 500) || attempt === 2) break;
          setProgress((p) => p ?? 0);
          await new Promise((r) => setTimeout(r, 4000 * (attempt + 1)));
        }
      }
      if (last) { error = `Import arrêté à la fiche ${i} : ${last.response?.data?.detail || "connexion perdue"}. Relancez : les fiches déjà remplies ne sont pas dupliquées.`; break; }
      setProgress(Math.round((Math.min(i + BATCH, list.length) / list.length) * 100));
    }
    await api.post("/migration/import/finish", { error }).catch(() => {});
    setProgress(null);
    refresh();
    if (error) toast.error(error); else toast.success(test ? "Test terminé : vérifiez les fiches." : "Import terminé.");
  };
  const c = st.counts;
  const busy = progress !== null || st.running;
  return (
    <section className="hm-card mg-import" data-testid="mg-import">
      <h3><FileUp size={17} /> Importer l'export noCRM (JSON)</h3>
      <p className="mg-help">Remplit les fiches pas encore copiées (fiche + commentaires), par paquets de {BATCH}. Les fiches déjà copiées gardent leurs commentaires avec auteurs. Les appels Ringover et l'activité du CRM ne sont jamais touchés. Une sauvegarde est faite avant chaque import ; les auteurs des commentaires sont ajoutés ensuite par le transfert du soir.</p>
      <div className="mg-import__row">
        <input type="file" accept=".json,application/json" disabled={busy} onChange={(e) => read(e.target.files[0])} data-testid="mg-import-file" />
        {reading && <span data-testid="mg-import-reading">Lecture du fichier…</span>}
        {rows && <span data-testid="mg-import-ready">{n(rows.length)} fiches prêtes</span>}
      </div>
      {rows && (
        <div className="mg-import__row">
          <input type="text" placeholder="N° noCRM des fiches à tester (ex. 39937498, 37387727)" value={ids} onChange={(e) => setIds(e.target.value)} data-testid="mg-import-test-ids" />
          <button type="button" className="secondary-button" disabled={busy} onClick={() => run(true)} data-testid="mg-import-test">Tester sur ces fiches</button>
          <button type="button" className="primary-button" disabled={busy} onClick={() => run(false)} data-testid="mg-import-all">Importer tout le fichier</button>
        </div>
      )}
      {progress !== null && <p data-testid="mg-import-running">Import en cours… {progress} % (ne fermez pas la page).</p>}
      {st.running && progress === null && <p className="pl-warning" data-testid="mg-import-stuck">Un import est marqué en cours. S'il a été interrompu, cliquez <button type="button" className="pl-link" onClick={() => api.post("/migration/import/finish", { error: "Interrompu" }).then(refresh)} data-testid="mg-import-reset">ici pour le clore</button>.</p>}
      {st.error && <p className="pl-warning" data-testid="mg-import-error">{st.error}</p>}
      {c && !st.running && st.finished_at && (
        <p data-testid="mg-import-report">Dernier import {st.mode === "test" ? `de test (${(st.only_ids || []).length} fiches)` : "complet"} ({new Date(st.finished_at).toLocaleString("fr-FR")}) : {n(c.file)} fiches lues, <b>{n(c.filled)} remplies</b> ({n(c.comments)} commentaires), {n(c.kept_with_authors)} déjà copiées gardées telles quelles, {n(c.unknown_ids)} inconnues du CRM.{st.backup && ` Sauvegarde : ${st.backup}.`}</p>
      )}
    </section>
  );
};
